/**
 * Custom hooks for common operations
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import ErrorHandler from '../utils/errorHandler';
import { useToast } from '../contexts/ToastContext';

// Simple in-memory cache and in-flight deduper per endpoint
const responseCache = new Map(); // key -> { data, ts }
const inflight = new Map(); // key -> promise

/**
 * Hook for fetching data from API
 * @param {string} url - API endpoint
 * @param {Object} options - Configuration options
 * @returns {Object} - { data, loading, error, refetch }
 */
export const useFetch = (url, options = {}) => {
  const {
    immediate = true,
    onSuccess = null,
    onError = null,
    defaultValue = null,
    transform = null,
    params = null,
    cacheTtl = 0,
    cacheKey = null,
  } = options;

  const [data, setData] = useState(defaultValue);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);
  const { showToast } = useToast();

  const dataRef = useRef(defaultValue);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Keep latest handlers without recreating fetchData
  const transformRef = useRef(transform);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const inFlightRef = useRef(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    transformRef.current = transform;
  }, [transform]);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const fetchData = useCallback(async () => {
    const key =
      cacheKey ||
      `${url}::${params && typeof params === 'object' ? JSON.stringify(params) : String(params ?? '')}`;

    // Serve from cache if fresh
    if (cacheTtl && responseCache.has(key)) {
      const cached = responseCache.get(key);
      if (Date.now() - cached.ts < cacheTtl) {
        setData(cached.data);
        setLoading(false);
        return cached.data;
      }
      responseCache.delete(key);
    }

    // Deduplicate in-flight requests (still update THIS hook's state)
    if (inflight.has(key)) {
      inFlightRef.current = true;
      try {
        setLoading(true);
        setError(null);

        const response = await inflight.get(key);
        const transformedData = transformRef.current
          ? transformRef.current(response.data)
          : response.data;

        setData(transformedData);

        if (cacheTtl) {
          responseCache.set(key, { data: transformedData, ts: Date.now() });
        }

        if (onSuccessRef.current) {
          onSuccessRef.current(transformedData);
        }

        return transformedData;
      } catch (err) {
        const errorMessage = ErrorHandler.handleApiError(err, showToast, `useFetch: ${url}`);
        setError(errorMessage);

        if (onErrorRef.current) {
          onErrorRef.current(err);
        }

        return null;
      } finally {
        inFlightRef.current = false;
        setLoading(false);
      }
    }

    if (inFlightRef.current) return dataRef.current;

    inFlightRef.current = true;
    try {
      setLoading(true);
      setError(null);

      const request = api.get(url, params ? { params } : undefined).finally(() => {
        inflight.delete(key);
      });
      inflight.set(key, request);

      const response = await request;
      const transformedData = transformRef.current
        ? transformRef.current(response.data)
        : response.data;

      setData(transformedData);

      if (cacheTtl) {
        responseCache.set(key, { data: transformedData, ts: Date.now() });
      }

      if (onSuccessRef.current) {
        onSuccessRef.current(transformedData);
      }

      return transformedData;
    } catch (err) {
      const errorMessage = ErrorHandler.handleApiError(err, showToast, `useFetch: ${url}`);
      setError(errorMessage);

      if (onErrorRef.current) {
        onErrorRef.current(err);
      }

      return null;
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [url, showToast, cacheKey, cacheTtl, params]);

  useEffect(() => {
    if (immediate && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchData();
    }
  }, [immediate, fetchData]);

  return { data, loading, error, refetch: fetchData };
};

/**
 * Hook for fetching multiple endpoints in parallel
 * @param {Array} endpoints - Array of endpoint strings
 * @param {Object} options - Configuration options
 * @returns {Object} - { data, loading, error, refetch }
 */
export const useFetchMultiple = (endpoints, options = {}) => {
  const { immediate = true, transforms = [], cacheTtl = 0, cacheKey = null } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);
  const { showToast } = useToast();

  const dataRef = useRef(null);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const transformsRef = useRef(transforms);
  const inFlightRef = useRef(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    transformsRef.current = transforms;
  }, [transforms]);

  const fetchData = useCallback(async () => {
    const key = cacheKey || endpoints.join('|');

    if (cacheTtl && responseCache.has(key)) {
      const cached = responseCache.get(key);
      if (Date.now() - cached.ts < cacheTtl) {
        setData(cached.data);
        setLoading(false);
        return cached.data;
      }
      responseCache.delete(key);
    }

    if (inflight.has(key)) {
      inFlightRef.current = true;
      try {
        setLoading(true);
        setError(null);

        const responses = await inflight.get(key);
        const results = responses.map((response, index) => {
          const transform = transformsRef.current[index];
          return transform ? transform(response.data) : response.data;
        });

        setData(results);
        if (cacheTtl) {
          responseCache.set(key, { data: results, ts: Date.now() });
        }

        return results;
      } catch (err) {
        const errorMessage = ErrorHandler.handleApiError(err, showToast, 'useFetchMultiple');
        setError(errorMessage);
        return null;
      } finally {
        inFlightRef.current = false;
        setLoading(false);
      }
    }

    if (inFlightRef.current) return dataRef.current;

    inFlightRef.current = true;
    try {
      setLoading(true);
      setError(null);

      const promise = Promise.all(endpoints.map((url) => api.get(url))).finally(() => {
        inflight.delete(key);
      });
      inflight.set(key, promise);

      const responses = await promise;

      const results = responses.map((response, index) => {
        const transform = transformsRef.current[index];
        return transform ? transform(response.data) : response.data;
      });

      setData(results);
      if (cacheTtl) {
        responseCache.set(key, { data: results, ts: Date.now() });
      }
      return results;
    } catch (err) {
      const errorMessage = ErrorHandler.handleApiError(err, showToast, 'useFetchMultiple');
      setError(errorMessage);
      return null;
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [endpoints, showToast, cacheKey, cacheTtl]);

  useEffect(() => {
    if (immediate && endpoints.length > 0 && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchData();
    }
  }, [immediate, fetchData, endpoints.length]);

  return { data, loading, error, refetch: fetchData };
};

/**
 * Hook for form submission
 * @param {Function} submitFn - Function that performs the submission
 * @param {Object} options - Configuration options
 * @returns {Object} - { submit, submitting, error }
 */
export const useSubmit = (submitFn, options = {}) => {
  const { onSuccess = null, onError = null, successMessage = 'Operation successful' } = options;

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { showToast } = useToast();

  const submit = useCallback(
    async (...args) => {
      try {
        setSubmitting(true);
        setError(null);

        const result = await submitFn(...args);

        if (successMessage) {
          showToast(successMessage, 'success');
        }

        if (onSuccess) {
          onSuccess(result);
        }

        return result;
      } catch (err) {
        const errorMessage = ErrorHandler.handleApiError(err, showToast, 'useSubmit');
        setError(errorMessage);

        if (onError) {
          onError(err);
        }

        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [submitFn, onSuccess, onError, successMessage, showToast]
  );

  return { submit, submitting, error };
};

/**
 * Hook for debounced value
 * @param {any} value - Value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {any} - Debounced value
 */
export const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Hook for local storage with state synchronization
 * @param {string} key - Storage key
 * @param {any} initialValue - Initial value
 * @returns {Array} - [storedValue, setValue]
 */
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      ErrorHandler.logError(error, 'useLocalStorage');
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (error) {
        ErrorHandler.logError(error, 'useLocalStorage');
      }
    },
    [key, storedValue]
  );

  return [storedValue, setValue];
};

/**
 * Hook for previous value
 * @param {any} value
 * @returns {any} - Previous value
 */
export const usePrevious = (value) => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

/**
 * Hook for window size
 * @returns {Object} - { width, height }
 */
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

export default {
  useFetch,
  useFetchMultiple,
  useSubmit,
  useDebounce,
  useLocalStorage,
  usePrevious,
  useWindowSize,
};
